import type en from "./catalogues/en";

/** The two interface languages. English is the default. */
export type Lang = "en" | "id";
export const LANGS: readonly Lang[] = ["en", "id"];
export const DEFAULT_LANG: Lang = "en";

export const isLang = (v: unknown): v is Lang => v === "en" || v === "id";

/** Every key of the English dictionary: a key missing from it fails typecheck. */
export type Key = keyof typeof en & string;
/** Any placeholder values, untyped: only for code that forwards them. */
export type Params = Record<string, string | number>;

/** The placeholder names of a literal text: "{count} of {total}" → "count" | "total". */
type NamesIn<S extends string> = S extends `${string}{${infer Name}}${infer Rest}` ? Name | NamesIn<Rest> : never;

/** The placeholder names of key's English text (the parity test holds the Indonesian text to the same names). */
export type Placeholders<K extends Key> = NamesIn<(typeof en)[K]>;

/** Optional, and empty: a key whose text has no placeholder takes no values. */
type NoParams = Partial<Record<string, never>>;

/**
 * The params of t(key): optional and empty when the English text has no
 * placeholder, else required with exactly its names. Distributes over a union
 * of keys (`t(statusKey)`), so a dynamic key is checked per possible key.
 */
export type TArgs<K extends Key> = K extends Key
  ? [Placeholders<K>] extends [never]
    ? [params?: NoParams]
    : [params: { [N in Placeholders<K>]: string | number }]
  : never;

/**
 * What every id.ts is typed as: exactly the keys of its English twin, any
 * text. tsc then rejects a missing or an extra key; the parity test checks
 * the placeholders.
 */
export type Catalogue<T> = { readonly [K in keyof T]: string };

type PluralBase<K> = K extends `${infer B}_one` ? B : never;
/** A key with `_one`/`_other` forms, named without the suffix. */
export type PluralKey = PluralBase<Key>;

type PluralNames<K extends PluralKey> = Exclude<
  Placeholders<Extract<`${K}_one` | `${K}_other`, Key>>,
  "count"
>;
/** The params of tn(key, count): `count` is passed on its own, every other placeholder here. */
export type TnArgs<K extends PluralKey> = K extends PluralKey
  ? [PluralNames<K>] extends [never]
    ? [params?: NoParams]
    : [params: { [N in PluralNames<K>]: string | number }]
  : never;

interface Active {
  lang: Lang;
  dict: Readonly<Record<string, string>>;
}

/**
 * One dictionary per page, kept on globalThis rather than in this module: a
 * second evaluation of this module (a hot reload, or a test that re-imports
 * the store graph with vi.resetModules to model another tab) must read the
 * dictionary the page booted with, not start empty and throw.
 */
const SLOT = Symbol.for("erp.i18n");
const page = globalThis as typeof globalThis & { [SLOT]?: Active };

/** Makes dict the dictionary t() reads. Called once at boot (and by tests). */
export function installDictionary(lang: Lang, dict: Readonly<Record<string, string>>): void {
  page[SLOT] = { lang, dict };
}

export function currentLang(): Lang {
  return page[SLOT]?.lang ?? DEFAULT_LANG;
}

const PLACEHOLDER = /\{(\w+)\}/g;

function lookup(key: string): string {
  const active = page[SLOT];
  if (!active) {
    // A label evaluated at module load before boot installed the dictionary
    // would freeze as a raw key for the life of the page, since switching
    // reloads rather than re-rendering labels. Make that
    // loud wherever someone can see it; production degrades to the key.
    if (import.meta.env.DEV || import.meta.env.MODE === "test") {
      throw new Error(`t("${key}") called before a dictionary was installed`);
    }
    return key;
  }
  return active.dict[key] ?? key;
}

function render(key: string, params?: Readonly<Partial<Params>>): string {
  const text = lookup(key);
  if (!params) return text;
  return text.replace(PLACEHOLDER, (whole, name: string) =>
    Object.hasOwn(params, name) && params[name] !== undefined ? String(params[name]) : whole,
  );
}

/** The text of key in the installed language, its placeholders filled from params. */
export function t<K extends Key>(key: K, ...args: TArgs<K>): string {
  return render(key, args[0]);
}

const pluralRules = new Map<Lang, Intl.PluralRules>();
const pluralRule = (lang: Lang) => {
  let rule = pluralRules.get(lang);
  if (!rule) pluralRules.set(lang, (rule = new Intl.PluralRules(lang)));
  return rule;
};

/** Count-dependent text: `<key>_one` or `<key>_other` by the active language's plural rule. */
export function tn<K extends PluralKey>(key: K, count: number, ...args: TnArgs<K>): string {
  const form = pluralRule(currentLang()).select(count) === "one" ? "one" : "other";
  return render(`${key}_${form}`, { ...args[0], count });
}

/** The placeholder names in text, once each, sorted (the parity test compares them). */
export function placeholders(text: string): string[] {
  return [...new Set([...text.matchAll(PLACEHOLDER)].map((m) => m[1]))].sort();
}
